import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Mic, Square, ArrowLeft, Sparkles, Undo2 } from "lucide-react";

import {
  structureMeetupGoals,
  transcribeMeetupAudio,
  updateGoalFields,
} from "@/lib/api/participants";
import { getParticipantSession } from "@/lib/participant-session";

import { Button } from "@/components/ui/Button";
import { Field } from "@/components/ui/Field";
import { Textarea } from "@/components/ui/Input";
import { ErrorBlock } from "@/components/ui/States";
import { InlineAlert } from "@/components/ui/States";
import { MobileFrame } from "@/components/ui/MobileFrame";
import { cn } from "@/lib/utils";

export function GoalsPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [lookingFor, setLookingFor] = useState("");
  const [canHelpWith, setCanHelpWith] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // AI structuring state. `structuring` is the spinner; `preStructure`
  // snapshots the raw text right before the AI call so the user can hit
  // "Revert" if the polish overshoots. Cleared when the user types again
  // (they're past the point of needing the undo).
  const [structuring, setStructuring] = useState(false);
  const [structureError, setStructureError] = useState<string | null>(null);
  const [preStructure, setPreStructure] = useState<
    { lookingFor: string; canHelpWith: string } | null
  >(null);

  useEffect(() => {
    if (!slug) return;
    const session = getParticipantSession(slug);
    if (!session) {
      navigate(`/e/${slug}`, { replace: true });
    }
  }, [slug, navigate]);

  // The wrappers below run on every keystroke / Speak-instead append. Once
  // the user has manually changed a value after a structuring pass, the
  // "Revert" link goes away — there's no meaningful original to go back to.
  function handleLookingForChange(next: string) {
    setLookingFor(next);
    if (preStructure !== null) setPreStructure(null);
  }
  function handleCanHelpWithChange(next: string) {
    setCanHelpWith(next);
    if (preStructure !== null) setPreStructure(null);
  }

  async function onStructure() {
    if (!slug) return;
    const session = getParticipantSession(slug);
    if (!session) {
      setError(
        "Your registration was lost on this device. Return to the event page and sign in again to continue.",
      );
      return;
    }
    if (lookingFor.trim().length === 0 && canHelpWith.trim().length === 0) {
      setStructureError("Type something in either field first — AI needs raw input to polish.");
      return;
    }
    setStructuring(true);
    setStructureError(null);
    try {
      const polished = await structureMeetupGoals(
        session.participantId,
        lookingFor,
        canHelpWith,
      );
      // Snapshot the user's raw input before overwriting so they can roll
      // back if the polished version overshoots.
      setPreStructure({ lookingFor, canHelpWith });
      setLookingFor(polished.lookingFor);
      setCanHelpWith(polished.canHelpWith);
    } catch (err) {
      console.error("[GoalsPage] structureMeetupGoals failed:", err);
      setStructureError(
        err instanceof Error ? err.message : "Couldn't structure the answers. Try again.",
      );
    } finally {
      setStructuring(false);
    }
  }

  function onRevertStructure() {
    if (!preStructure) return;
    setLookingFor(preStructure.lookingFor);
    setCanHelpWith(preStructure.canHelpWith);
    setPreStructure(null);
    setStructureError(null);
  }

  const onContinue = async () => {
    if (!slug) {
      setError("Missing event identifier — reload the page.");
      return;
    }
    const session = getParticipantSession(slug);
    if (!session) {
      // Don't silently bounce back to /e/:slug — the user will swear the
      // button is broken. Surface what happened so they can act on it.
      setError(
        "Your registration was lost on this device. Return to the event page and sign in again to continue.",
      );
      return;
    }
    if (lookingFor.trim().length === 0 || canHelpWith.trim().length === 0) {
      setError("Please fill in both fields before continuing.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await updateGoalFields(
        session.participantId,
        lookingFor.trim(),
        canHelpWith.trim(),
      );
      // V2 spec step 6: route through the Pitch Advice upsell screen.
      // It auto-skips for participants who already have a video, so
      // returning users who re-edit goals aren't bounced through it
      // a second time. Picking 'Maybe later' on the upsell drops the
      // participant into /feed as a text-only card.
      navigate(`/e/${slug}/pitch-advice`);
    } catch (err) {
      // Logged so we can diagnose if the user reports a silent "didn't
      // work" — the displayed message is for the user, the console one is
      // for us in DevTools.
      console.error("[GoalsPage] updateGoalFields failed:", err);
      setError(err instanceof Error ? err.message : "Failed to save. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const canContinue = lookingFor.trim().length > 0 && canHelpWith.trim().length > 0;

  return (
    <MobileFrame>
      <div className="flex-1 px-4 py-6 animate-fade-in">
        <h1 className="text-[24px] font-bold tracking-tight text-[#1a1a1a]">
          Your goals for this event
        </h1>
        <p className="mt-2 text-[14px] text-[#6c757d]">
          Be specific — concrete answers get concrete matches.
        </p>

        <div className="mt-6 flex flex-col gap-5">
          <DictatedField
            slug={slug ?? ""}
            label="What are you looking for?"
            placeholder="B2B sales advisor with SaaS experience, technical co-founder with payments expertise..."
            value={lookingFor}
            onChange={handleLookingForChange}
          />

          <DictatedField
            slug={slug ?? ""}
            label="What can you help others with?"
            placeholder="B2B sales strategy 0–$5M ARR, legal advice for tech startups..."
            value={canHelpWith}
            onChange={handleCanHelpWithChange}
          />

          <InlineAlert tone="warning">
            These fields run alongside what AI extracts from your pitch. The clearer you are, the better your matches.
          </InlineAlert>

          {/* AI structuring CTA. Sits between the warning and the
              Back/Continue row so the user reads "be clear" → "let AI
              clean it up" → "Continue" in order. After a structuring pass,
              a Revert link appears underneath so people aren't trapped
              with a rewrite they don't like. Typing in either field
              clears the snapshot (handled in handle*Change). */}
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={onStructure}
              disabled={
                structuring ||
                (lookingFor.trim().length === 0 && canHelpWith.trim().length === 0)
              }
              className={cn(
                "flex w-full items-center justify-center gap-2 rounded-[12px] border border-dashed border-[#bfdbfe] bg-[#eff6ff] px-4 py-3 text-[14px] font-semibold text-[#2563eb]",
                "transition-colors hover:bg-[#dbeafe] disabled:cursor-not-allowed disabled:opacity-50",
              )}
            >
              <Sparkles className="h-4 w-4" />
              {structuring
                ? "Structuring with AI…"
                : preStructure
                  ? "Re-structure with AI"
                  : "Structure with AI"}
            </button>
            <p className="px-1 text-[11px] text-[#94a3b8]">
              {structuring
                ? "Reading what you wrote and polishing the wording — meaning stays the same."
                : preStructure
                  ? "Polished version applied. Edit anything below, or revert to your original wording."
                  : "AI will tighten your phrasing without inventing facts. You can still edit anything after."}
            </p>
            {preStructure ? (
              <button
                type="button"
                onClick={onRevertStructure}
                className="self-start inline-flex items-center gap-1 px-1 text-[12px] font-semibold text-[#475569] hover:text-[#1a1a1a]"
              >
                <Undo2 className="h-3 w-3" /> Revert to your original wording
              </button>
            ) : null}
            {structureError ? (
              <p className="px-1 text-[12px] text-[#b91c1c]">{structureError}</p>
            ) : null}
          </div>

          {/* Errors live RIGHT next to the buttons — otherwise on a mobile
              viewport the message scrolls offscreen and Continue looks
              broken when in fact the call surfaced a problem. */}
          {error ? <ErrorBlock title="Could not save" message={error} /> : null}

          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={() => navigate(-1)}
              leadingIcon={<ArrowLeft className="h-4 w-4" />}
            >
              Back
            </Button>
            <Button onClick={onContinue} loading={submitting} disabled={!canContinue} fullWidth>
              {submitting ? "Saving..." : "Continue"}
            </Button>
          </div>
        </div>
      </div>
    </MobileFrame>
  );
}

interface DictatedFieldProps {
  slug: string;
  label: string;
  placeholder: string;
  value: string;
  onChange: (next: string) => void;
}

type RecordState = "idle" | "recording" | "transcribing";

function pickAudioMimeType(): string {
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/mpeg",
  ];
  if (typeof MediaRecorder === "undefined") return "";
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) ?? "";
}

function DictatedField({ slug, label, placeholder, value, onChange }: DictatedFieldProps) {
  const [state, setState] = useState<RecordState>("idle");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const stopTracks = () => {
    const stream = recorderRef.current?.stream;
    stream?.getTracks().forEach((t) => t.stop());
  };

  const startRecording = async () => {
    setFieldError(null);
    const session = getParticipantSession(slug);
    if (!session) {
      setFieldError("Session expired — reload the page and register again.");
      return;
    }
    if (typeof MediaRecorder === "undefined") {
      setFieldError("Voice input isn't supported on this browser — type your answer instead.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = pickAudioMimeType();
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stopTracks();
        const type = recorder.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        chunksRef.current = [];
        if (blob.size === 0) {
          setState("idle");
          return;
        }
        setState("transcribing");
        try {
          const text = await transcribeMeetupAudio(session.participantId, blob);
          const next = value.trim() ? `${value.trim()} ${text}`.trim() : text;
          onChange(next);
        } catch (err) {
          setFieldError(err instanceof Error ? err.message : "Could not transcribe audio.");
        } finally {
          setState("idle");
        }
      };
      recorderRef.current = recorder;
      recorder.start();
      setState("recording");
    } catch (err) {
      setFieldError(
        err instanceof Error
          ? `Microphone access denied: ${err.message}`
          : "Microphone access denied.",
      );
    }
  };

  const stopRecording = () => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") recorder.stop();
  };

  useEffect(() => () => stopTracks(), []);

  // The Speak button used to sit absolutely over the bottom-right of the
  // textarea. On phones that meant the button was permanently covering
  // whatever the user was typing in the last line — the cursor literally
  // disappeared behind it. Moving the button below the textarea in its
  // own row sacrifices a tiny bit of vertical space but the input stays
  // fully readable while typing.
  return (
    <Field label={label} hint={state === "recording" ? "Listening — tap to stop" : undefined}>
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={4}
      />
      <div className="mt-2 flex justify-end">
        <button
          type="button"
          onClick={state === "recording" ? stopRecording : startRecording}
          disabled={state === "transcribing"}
          aria-label={state === "recording" ? "Stop dictation" : "Speak instead"}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-[10px] px-3 py-1.5 text-[12px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60",
            state === "recording"
              ? "bg-[#ef4444] text-white animate-pulse-ring"
              : "border border-[#e2e8f0] bg-white text-[#2563eb] hover:bg-[#f8fafc]",
          )}
        >
          {state === "recording" ? (
            <>
              <Square className="h-3 w-3" fill="currentColor" /> Stop
            </>
          ) : state === "transcribing" ? (
            "Transcribing…"
          ) : (
            <>
              <Mic className="h-3.5 w-3.5" /> Speak instead
            </>
          )}
        </button>
      </div>
      {fieldError ? (
        <p className="mt-1.5 text-[12px] text-[#991b1b]">{fieldError}</p>
      ) : null}
    </Field>
  );
}
